import React from 'react';
import { Link } from 'react-router-dom';
import type { QuoteCreatedRecords } from '../../types/quote-pricing';
import { fmtUnitRate } from '../../lib/display-labels';
import { formatCalendarDate } from '../../lib/calendar-date';
import { humanizeLabel } from '../../lib/reports';
import { DH_PERSONNEL_ROLE_LABEL, dhLabel } from '../../lib/downhole-labels';

/**
 * "Created from this quote" — every record an accepted quote made, read from
 * GET /quotes/:id/created-records so the summary survives a reload rather than
 * living only in the accept response.
 *
 *   casing           the job (pinned to the quote's rate sheet) and/or the invoice
 *   downhole         the customer tool rates the binding accept set, the assembly,
 *                    and the customer crew (personnel) rates (api#1092)
 *   pressure testing the PT job
 *   NDT inspection   the NDT job (api#1166)
 *   Pipeline         the Pipeline job (api#1232)
 *   Well Testing     the Well Testing job (api#1258)
 *   Well Servicing   the Well Servicing job (api#1295)
 *   surface rentals  the rental order (no detail screen to link to yet — text only)
 *
 * Renders nothing when the quote made nothing. Absent values are an em dash.
 */

const RATE_FIELDS: Array<[keyof QuoteCreatedRecords['toolRates'][number], string]> = [
  ['run_rate', 'per C&D run'],
  ['hourly_rate', 'per hour'],
  ['day_rate', 'per day'],
  ['footage_rate', 'per foot'],
  ['minimum_charge', 'minimum'],
  ['lih_rate', 'lost in hole'],
];

type PersonnelRate = NonNullable<QuoteCreatedRecords['personnelRates']>[number];
const PERSONNEL_FIELDS: Array<[keyof PersonnelRate, string]> = [
  ['day_rate', 'per day'],
  ['standby_day_rate', 'per standby day'],
  ['travel_day_rate', 'per travel day'],
];

const day = (d: string | null) => formatCalendarDate(d) ?? '—';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
      <span className="text-fieldiq-text">{label}</span>
      <span className="font-medium text-white">{children}</span>
    </li>
  );
}

const LINK = 'font-semibold text-fieldiq-accent underline-offset-2 hover:underline';

export function hasCreatedRecords(r: QuoteCreatedRecords | null | undefined): boolean {
  if (!r) return false;
  return r.jobs.length + r.invoices.length + r.toolRates.length + r.assemblies.length + r.ptJobs.length + r.rentalOrders.length
    + (r.ndtJobs?.length ?? 0)
    + (r.pfJobs?.length ?? 0)
    + (r.wtJobs?.length ?? 0)
    + (r.ewJobs?.length ?? 0)
    + (r.wsJobs?.length ?? 0)
    + (r.personnelRates?.length ?? 0) > 0;
}

export function QuoteCreatedRecordsPanel({ records }: { records: QuoteCreatedRecords }) {
  if (!hasCreatedRecords(records)) return null;
  const window = records.toolRates[0];
  const crew = records.personnelRates ?? [];
  return (
    <section
      data-testid="quote-created-records"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Accepted quote</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">Created from this quote</h2>
      </div>
      <ul className="divide-y divide-fieldiq-border">
        {records.jobs.map((j) => (
          <Row key={j.id} label="Job">
            <Link to={`/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">
              priced from {j.rate_sheet_name ? `"${j.rate_sheet_name}"` : '—'} · {humanizeLabel(j.status)}
            </span>
          </Row>
        ))}
        {records.invoices.map((i) => (
          <Row key={i.id} label="Invoice">
            <Link to={`/invoices/${i.id}`} className={LINK}>{i.invoice_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(i.status)}</span>
          </Row>
        ))}
        {records.ptJobs.map((j) => (
          <Row key={j.id} label="PT job">
            <Link to={`/pressure-testing/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.scheduled_date)}</span>
          </Row>
        ))}
        {(records.ndtJobs ?? []).map((j) => (
          <Row key={j.id} label="NDT job">
            <Link to={`/ndt-inspection/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.scheduled_date)}</span>
          </Row>
        ))}
        {(records.pfJobs ?? []).map((j) => (
          <Row key={j.id} label="Pipeline job">
            <Link to={`/pipeline-facility/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.scheduled_start)}</span>
          </Row>
        ))}
        {(records.wtJobs ?? []).map((j) => (
          <Row key={j.id} label="Well Testing job">
            <Link to={`/well-testing/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.planned_start_on)}</span>
          </Row>
        ))}
        {(records.ewJobs ?? []).map((j) => (
          <Row key={j.id} label="Earthworks job">
            <Link to={`/earthworks/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.scheduled_start)}</span>
          </Row>
        ))}
        {(records.wsJobs ?? []).map((j) => (
          <Row key={j.id} label="Well Servicing job">
            <Link to={`/well-servicing/jobs/${j.id}`} className={LINK}>{j.job_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(j.status)} · {day(j.planned_start_on)}</span>
          </Row>
        ))}
        {records.rentalOrders.map((o) => (
          <Row key={o.id} label="Rental order">
            {o.order_number}
            <span className="ml-2 text-fieldiq-muted">{humanizeLabel(o.status)}</span>
          </Row>
        ))}
        {records.assemblies.map((a) => (
          <Row key={a.id} label="Assembly">
            <Link to={`/downhole/assemblies/${a.id}/build`} className={LINK}>{a.assembly_number}</Link>
            <span className="ml-2 text-fieldiq-muted">{a.name} · {humanizeLabel(a.status)}</span>
          </Row>
        ))}
      </ul>
      {records.toolRates.length > 0 && (
        <div className="border-t border-fieldiq-border px-4 py-3" data-testid="quote-created-tool-rates">
          <p className="text-sm font-semibold text-white">
            Customer tool rates, {day(window.effective_date)} to {day(window.expiry_date)}
          </p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            Runs for this customer in that window price at these rates. A tool or rate type not listed here
            prices at the standard rate, and its ticket line says "Not on quote: standard rate".
          </p>
          <ul className="mt-2 space-y-1.5">
            {records.toolRates.map((t) => (
              <li key={t.id} className="text-sm">
                <span className="font-medium text-white">{t.serial_number ?? humanizeLabel(t.category ?? '')}</span>
                <span className="ml-2 text-fieldiq-text">
                  {RATE_FIELDS.filter(([f]) => t[f] != null)
                    .map(([f, words]) => `${fmtUnitRate(t[f] as string, t.currency)} ${words}`)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {crew.length > 0 && (
        <div className="border-t border-fieldiq-border px-4 py-3" data-testid="quote-created-personnel-rates">
          <p className="text-sm font-semibold text-white">
            Customer crew rates, {day(crew[0].effective_date)} to {day(crew[0].expiry_date)}
          </p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            Well-job days for this customer in that window price each role at these rates. A rate not listed here
            prices at the role's standard rate, and the day line says "Not on quote: standard rate".
          </p>
          <ul className="mt-2 space-y-1.5">
            {crew.map((p) => (
              <li key={p.id} className="text-sm">
                <span className="font-medium text-white">{dhLabel(DH_PERSONNEL_ROLE_LABEL, p.personnel_role)}</span>
                <span className="ml-2 text-fieldiq-text">
                  {PERSONNEL_FIELDS.filter(([f]) => p[f] != null)
                    .map(([f, words]) => `${fmtUnitRate(p[f] as string, p.currency)} ${words}`)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
